import { useState, useEffect, useMemo } from 'react'
import { normalizeStatus } from 'utils/certificateUtils'
import { ALLOWED_TABS } from 'constants/certificateConstants'
import type { CerteficateTab } from 'types'

export interface TabSummary {
  label: string
  count: number
}

export interface UseCertificateTabsProps {
  baseCertificates: any[]
  isRefetching: boolean
  isLoading: boolean
  hasActiveFilters?: boolean
  originalCertificates?: any[]
}

export const useCertificateTabs = ({
  baseCertificates,
  isRefetching,
  isLoading,
  hasActiveFilters = false,
  originalCertificates = [],
}: UseCertificateTabsProps) => {
  const [stableTabsSummaryList, setStableTabsSummaryList] = useState<TabSummary[]>([])
  const [previousHasActiveFilters, setPreviousHasActiveFilters] = useState<boolean>(false)
  const [initialTabsSummaryList, setInitialTabsSummaryList] = useState<TabSummary[] | null>(null)

  const computeCounts = (base: any[]): TabSummary[] => {
    const countBy = (status: string) => 
      base.filter((c: any) => normalizeStatus(c?.status) === status).length
    
    const transferStates = ['out_transfer', 'waiting_accept', 'transfer_initiated', 'transfer_waiting_confirmation', 'in_transfer']
    const transferCount = base.filter((c: any) => transferStates.includes(normalizeStatus(c?.status))).length
    
    return [
      { label: 'all', count: base.length },
      { label: 'active', count: countBy('active') },
      { label: 'transfer', count: transferCount },
      { label: 'stolen', count: countBy('stolen') },
      { label: 'cancel request', count: countBy('cancel request') },
      { label: 'lost', count: countBy('lost') },
    ]
  }

  const tabsSummaryListCurrent = useMemo(() => {
    const source = hasActiveFilters ? (baseCertificates ?? []) : (originalCertificates ?? baseCertificates ?? [])
    return computeCounts(source)
  }, [baseCertificates, originalCertificates, hasActiveFilters])

  useEffect(() => {
    if (isRefetching || isLoading) return
    if (!hasActiveFilters && !initialTabsSummaryList) {
      const initSource = (originalCertificates ?? baseCertificates ?? [])
      const initCounts = computeCounts(initSource)
      setInitialTabsSummaryList(initCounts)
      if (stableTabsSummaryList.length === 0) {
        setStableTabsSummaryList(initCounts)
      }
      return
    }

    if (!hasActiveFilters && initialTabsSummaryList) {
      setStableTabsSummaryList(initialTabsSummaryList)
      return
    }

    if (hasActiveFilters) {
      setStableTabsSummaryList(tabsSummaryListCurrent)
    }
  }, [
    baseCertificates,
    isRefetching,
    isLoading,
    hasActiveFilters,
    initialTabsSummaryList,
  ])

  useEffect(() => {
    setPreviousHasActiveFilters(hasActiveFilters)
  }, [hasActiveFilters])

  const getTabCount = (tab: CerteficateTab): number => {
    const summary = stableTabsSummaryList.find(s => s.label === tab)
    return summary?.count ?? 0
  }

  const getAllTabCounts = (): Record<CerteficateTab, number> => {
    const counts = {} as Record<CerteficateTab, number>
    ALLOWED_TABS.forEach(tab => {
      counts[tab] = getTabCount(tab)
    })
    return counts
  }

  return {
    tabsSummaryList: stableTabsSummaryList,
    tabsSummaryListCurrent,
    getTabCount,
    getAllTabCounts,
    computeCounts,
  }
}
